// AppointmentBookingForm.js
import { useState } from "react";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import { State, City, Country } from "country-state-city";
import * as Yup from "yup";
import { FiLock } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import UserInfo from "./UserInfo";
import AddressInfo from "./AddressInfo";
import Payment from "./Payment";
import Seo from "../../seo/Seo";
import ProgressBar from "./ProgressBar";
import "./formStep.scss";
import LoadingSpinner from "../../loader/Loader";
import { useCreateSubscriptionMutation } from "../../../features/api/subscriptionApiSlice";
import PlanSelection from "./PlanSelection";
import AgreeTermsCheckbox from "./AgreeTemsCheckbox";
import { useVideo } from "../../../hooks/useVideo";

import { useRecaptcha, executeRecaptcha } from "../../../hooks/useRecaptcha";
import { useRef } from "react";

const SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

// Step-based validation schema
// Updated step-based validation schema
const getInputValidationSchema = (step) => {
  switch (step) {
    case 1:
      return Yup.object({
        plan: Yup.string().required("Please select a plan"),
      });

    case 2:
      return Yup.object({
        firstName: Yup.string().required(),
        lastName: Yup.string().required(),
        email: Yup.string().email().required(),
        password: Yup.string().min(8).required(),
        confirmPassword: Yup.string()
          .oneOf([Yup.ref("password"), null], "Passwords must match")
          .required(),
      });

    case 3:
      return Yup.object({
        address: Yup.string().required("Address is required"),
        city: Yup.string().required("City is required"),
        state: Yup.string().required("State is required"),
        zipCode: Yup.string().required("Zip code is required"),
      });

    case 4:
      return Yup.object({}); // ❗ Accept.js handles validation

    case 5:
      return Yup.object({
        agreeToPrivacy: Yup.boolean()
          .oneOf([true], "You must accept the privacy policy")
          .required(),
      });

    default:
      return Yup.object();
  }
};

const SubscriptionForm = () => {
  const { scrollToTop } = useVideo();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  const generatePaymentTokenRef = useRef(null);
  const [createSubscription, { isLoading }] = useCreateSubscriptionMutation();

  useRecaptcha(SITE_KEY, ["/member"]); // ✅ only show badge on /member

  // --------------------------------------------
  // INITIAL VALUES — cleaned
  // --------------------------------------------
  const initialValues = {
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    opaqueData: null, // ⭐ ONLY TOKEN WE NEED
    address: "",
    country: "US",
    city: "",
    state: "",
    zipCode: "",
    agreeToPrivacy: false,
    plan: "",
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    setFieldValue,
    validateForm,
    setTouched,
  } = useFormik({
    initialValues,
    validationSchema: getInputValidationSchema(step),
    onSubmit: async (values) => {
      // STEP 1–2 → move to next
      if (step < 5) {
        handleNext();
        return;
      }

      // ⭐ STEP 4 (final) → MUST HAVE CARD TOKEN
      if (step === 4 && !values.opaqueData) {
        toast.error("Please enter a valid card before continuing.");
        return;
      }

      // ‼️ NOW SAFE TO BUILD DATA
      const subscriptionData = {
        ...values,
        amount:
          values.plan === "eNotes Unlimited"
            ? 9.95
            : values.plan === "Standard"
            ? 19.99
            : 29.99,
      };

      try {
        const token = await executeRecaptcha(SITE_KEY, "member");

        const response = await createSubscription({
          ...subscriptionData,
          recaptchaToken: token,
        }).unwrap();

        toast.success(response?.message || response?.data?.message);
        navigate("/login");
      } catch (error) {
        console.log("FULL ERROR:", error);
        const errorMessage =
          error?.data?.message ||
          error?.message ||
          "An error occurred, please try again.";
        toast.error(errorMessage);
      }
    },

    enableReinitialize: true,
    validateOnChange: false, // Disable validation on every change
    validateOnBlur: false, // Disable validation on blur
  });

  if (isLoading) return <LoadingSpinner />;

  // --------------------------------------------
  // STEP FIELDS — updated
  // --------------------------------------------
  const stepFields = {
    1: ["plan"],
    2: ["firstName", "lastName", "email", "password", "confirmPassword"],
    3: ["address", "city", "state", "zipCode"],
    4: [], // ❗ Let Accept.js validate card
    5: ["agreeToPrivacy"],
  };

  const isStepValid = () => {
    const fields = stepFields[step] || [];
    return fields.every(
      (field) => values[field] && !errors[field] // must have value and no error
    );
  };

  const handleNext = async () => {
    // STEP 4 → Payment
    if (step === 4) {
      if (!generatePaymentTokenRef.current) {
        toast.error("Payment form not ready.");
        return;
      }

      if (!values.opaqueData) {
        // Trigger Accept.js token generation
        generatePaymentTokenRef.current();
        return; // wait for onTokenReady
      }
    }

    const validationErrors = await validateForm();

    if (Object.keys(validationErrors).length === 0) {
      setStep(step + 1);
      scrollToTop();
    } else {
      setTouched(
        Object.fromEntries(
          Object.keys(validationErrors).map((key) => [key, true])
        )
      );
    }
  };

  const handleBack = () => {
    setStep(step - 1);
    scrollToTop();
  };

  const totalSteps = 5;

  return (
    <div className="main_wrapper">
      <Seo
        title="Subscribe to Tips Tools Techniques | Woodsmith Store"
        description="Subscribe to Tips Tools Techniques video plans and gain unlimited access to woodworking tutorials, tools, and techniques."
        url="https://tipstoolstechnique.com/subscribe"
      />
      <div className="subscription-form">
        <ProgressBar currentStep={step} totalSteps={totalSteps} />
        <div className="form-wrapper">
          <form onSubmit={handleSubmit} autoComplete="on">
            {step === 1 && (
              <PlanSelection values={values} setFieldValue={setFieldValue} />
            )}
            {step === 2 && (
              <UserInfo
                values={values}
                errors={errors}
                touched={touched}
                setFieldValue={setFieldValue}
                handleChange={handleChange}
              />
            )}

            {step === 3 && (
              <AddressInfo
                values={values}
                errors={errors}
                touched={touched}
                Country={Country}
                State={State}
                City={City}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
              />
            )}

            {step === 4 && (
              <Payment
                values={values}
                registerTokenGenerator={(fn) => {
                  generatePaymentTokenRef.current = fn;
                }}
                onTokenReady={(opaqueData) => {
                  setFieldValue("opaqueData", opaqueData);
                }}
              />
            )}

            {/* <Payment
              onTokenReady={(opaqueData) => {
                setFieldValue("opaqueData", opaqueData);
                handleNext();
              }}
            /> */}

            {step === 5 && (
              <>
                <div className="review-summary">
                  <h3>Review Your Subscription</h3>

                  <div className="review-card">
                    <h4>Plan</h4>
                    <p>
                      {values.plan} - $
                      {values.plan === "eNotes Unlimited"
                        ? "9.95"
                        : values.plan === "Standard"
                        ? "19.99"
                        : "29.99"}
                      /year
                    </p>
                  </div>

                  <div className="review-card">
                    <h4>User Information</h4>
                    <p>
                      {values.firstName} {values.lastName}
                    </p>
                    <p>{values.email}</p>
                  </div>
                  <div className="review-card">
                    <h4>Payment Method</h4>
                    <p>Card information securely provided.</p>
                    <p>We do not store or display card details.</p>
                  </div>
                </div>

                <div className="privacy-step">
                  <AgreeTermsCheckbox
                    id="agreeToPrivacy"
                    // label="I agree to the Privacy Policy and Terms of Service."
                    checked={values.agreeToPrivacy}
                    onChange={(e) =>
                      setFieldValue("agreeToPrivacy", e.target.checked)
                    }
                    error={touched.agreeToPrivacy && errors.agreeToPrivacy}
                  />

                  <p className="terms-message">
                    By checking this box, you confirm that you have read and
                    agree to our{" "}
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </a>{" "}
                    and{" "}
                    <a href="/terms" target="_blank" rel="noopener noreferrer">
                      Terms of Service
                    </a>
                    . You also authorize <strong>enotesUnlimited</strong> to
                    securely process your subscription payment of $
                    {values.plan === "eNotes Unlimited"
                      ? "9.95"
                      : values.plan === "Standard"
                      ? "19.99"
                      : "29.99"}
                    /year.
                  </p>
                  <p
                    style={{
                      fontSize: "13px",
                      color: "#444",
                      marginTop: "10px",
                    }}
                  >
                    To verify your card, a temporary $1 authorization may appear
                    on your bank statement. This is{" "}
                    <strong>not a charge</strong> and will disappear
                    automatically. Your actual subscription fee of{" "}
                    <strong>$9.95</strong> will be processed after verification.
                  </p>
                </div>
              </>
            )}

            <div className="next-prev-button">
              {step > 1 && (
                <button type="button" onClick={handleBack}>
                  Back
                </button>
              )}
              <button
                type="submit"
                disabled={!isStepValid()}
                className={`next-button ${!isStepValid() ? "disabled" : ""}`}
              >
                {step === 5 ? (
                  <>
                    <FiLock aria-hidden="true" /> Pay Now
                  </>
                ) : (
                  "Next"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionForm;

// // AppointmentBookingForm.js
// import { useState } from "react";
// import { useFormik } from "formik";
// import toast from "react-hot-toast";
// import { State, City, Country } from "country-state-city";
// import * as Yup from "yup";
// import { FiLock } from "react-icons/fi";
// import { useNavigate } from "react-router-dom";
// import UserInfo from "./UserInfo";
// import AddressInfo from "./AddressInfo";
// import Payment from "./Payment";
// import Seo from "../../seo/Seo";
// import ProgressBar from "./ProgressBar";
// import "./formStep.scss";
// import LoadingSpinner from "../../loader/Loader";
// import { useCreateSubscriptionMutation } from "../../../features/api/subscriptionApiSlice";
// import PlanSelection from "./PlanSelection";
// import AgreeTermsCheckbox from "./AgreeTemsCheckbox";
// import { useVideo } from "../../../hooks/useVideo";

// import { useRecaptcha, executeRecaptcha } from "../../../hooks/useRecaptcha";

// const SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

// // Step-based validation schema
// // Updated step-based validation schema
// const getInputValidationSchema = (step) => {
//   switch (step) {
//     case 1:
//       return Yup.object({
//         plan: Yup.string().required("Please select a plan"),
//       });

//     case 2:
//       return Yup.object({
//         firstName: Yup.string().required(),
//         lastName: Yup.string().required(),
//         email: Yup.string().email().required(),
//         password: Yup.string().min(8).required(),
//         confirmPassword: Yup.string()
//           .oneOf([Yup.ref("password"), null], "Passwords must match")
//           .required(),
//       });

//     case 3:
//       return Yup.object({
//         address: Yup.string().required("Address is required"),
//         city: Yup.string().required("City is required"),
//         state: Yup.string().required("State is required"),
//         zipCode: Yup.string().required("Zip code is required"),
//       });

//     case 4:
//       return Yup.object({}); // ❗ Accept.js handles validation

//     case 5:
//       return Yup.object({
//         agreeToPrivacy: Yup.boolean()
//           .oneOf([true], "You must accept the privacy policy")
//           .required(),
//       });

//     default:
//       return Yup.object();
//   }
// };

// const SubscriptionForm = () => {
//   const { scrollToTop } = useVideo();
//   const navigate = useNavigate();
//   const [step, setStep] = useState(1);

//   const [createSubscription, { isLoading }] = useCreateSubscriptionMutation();

//   useRecaptcha(SITE_KEY, ["/member"]); // ✅ only show badge on /member

//   // --------------------------------------------
//   // INITIAL VALUES — cleaned
//   // --------------------------------------------
//   const initialValues = {
//     firstName: "",
//     lastName: "",
//     email: "",
//     password: "",
//     confirmPassword: "",
//     opaqueData: null, // ⭐ ONLY TOKEN WE NEED
//     address: "",
//     country: "US",
//     city: "",
//     state: "",
//     zipCode: "",
//     agreeToPrivacy: false,
//     plan: "",
//   };

//   const {
//     values,
//     errors,
//     touched,
//     handleChange,
//     handleSubmit,
//     setFieldValue,
//     validateForm,
//     setTouched,
//   } = useFormik({
//     initialValues,
//     validationSchema: getInputValidationSchema(step),
//     onSubmit: async (values) => {
//       // STEP 1–2 → move to next
//       if (step < 5) {
//         handleNext();
//         return;
//       }

//       // ⭐ STEP 4 (final) → MUST HAVE CARD TOKEN
//       if (step === 4 && !values.opaqueData) {
//         toast.error("Please enter a valid card before continuing.");
//         return;
//       }

//       // ‼️ NOW SAFE TO BUILD DATA
//       const subscriptionData = {
//         ...values,
//         amount:
//           values.plan === "eNotes Unlimited"
//             ? 9.95
//             : values.plan === "Standard"
//             ? 19.99
//             : 29.99,
//       };

//       try {
//         const token = await executeRecaptcha(SITE_KEY, "member");

//         const response = await createSubscription({
//           ...subscriptionData,
//           recaptchaToken: token,
//         }).unwrap();

//         toast.success(response?.message || response?.data?.message);
//         navigate("/login");
//       } catch (error) {
//         console.log("FULL ERROR:", error);
//         const errorMessage =
//           error?.data?.message ||
//           error?.message ||
//           "An error occurred, please try again.";
//         toast.error(errorMessage);
//       }
//     },

//     enableReinitialize: true,
//     validateOnChange: false, // Disable validation on every change
//     validateOnBlur: false, // Disable validation on blur
//   });

//   if (isLoading) return <LoadingSpinner />;

//   // --------------------------------------------
//   // STEP FIELDS — updated
//   // --------------------------------------------
//   const stepFields = {
//     1: ["plan"],
//     2: ["firstName", "lastName", "email", "password", "confirmPassword"],
//     3: ["address", "city", "state", "zipCode"],
//     4: [], // ❗ Let Accept.js validate card
//     5: ["agreeToPrivacy"],
//   };

//   const isStepValid = () => {
//     const fields = stepFields[step] || [];
//     return fields.every(
//       (field) => values[field] && !errors[field] // must have value and no error
//     );
//   };

//   const handleNext = async () => {
//     const validationErrors = await validateForm();
//     if (Object.keys(validationErrors).length === 0) {
//       setStep(step + 1);
//       scrollToTop();
//     } else {
//       setTouched(
//         Object.fromEntries(
//           Object.keys(validationErrors).map((key) => [key, true])
//         )
//       );
//     }
//   };

//   const handleBack = () => {
//     setStep(step - 1);
//     scrollToTop();
//   };

//   const totalSteps = 5;

//   return (
//     <div className="main_wrapper">
//       <Seo
//         title="Subscribe to Tips Tools Techniques | Woodsmith Store"
//         description="Subscribe to Tips Tools Techniques video plans and gain unlimited access to woodworking tutorials, tools, and techniques."
//         url="https://tipstoolstechnique.com/subscribe"
//       />
//       <div className="subscription-form">
//         <ProgressBar currentStep={step} totalSteps={totalSteps} />
//         <div className="form-wrapper">
//           <form onSubmit={handleSubmit} autoComplete="on">
//             {step === 1 && (
//               <PlanSelection values={values} setFieldValue={setFieldValue} />
//             )}
//             {step === 2 && (
//               <UserInfo
//                 values={values}
//                 errors={errors}
//                 touched={touched}
//                 setFieldValue={setFieldValue}
//                 handleChange={handleChange}
//               />
//             )}

//             {step === 3 && (
//               <AddressInfo
//                 values={values}
//                 errors={errors}
//                 touched={touched}
//                 Country={Country}
//                 State={State}
//                 City={City}
//                 handleChange={handleChange}
//                 setFieldValue={setFieldValue}
//               />
//             )}

//             {step === 4 && (
//               <Payment
//                 values={values} // 👈 add this
//                 onTokenReady={(opaqueData) => {
//                   setFieldValue("opaqueData", opaqueData);
//                   handleNext();
//                 }}
//               />
//             )}

//             {/* <Payment
//               onTokenReady={(opaqueData) => {
//                 setFieldValue("opaqueData", opaqueData);
//                 handleNext();
//               }}
//             /> */}

//             {step === 5 && (
//               <>
//                 <div className="review-summary">
//                   <h3>Review Your Subscription</h3>

//                   <div className="review-card">
//                     <h4>Plan</h4>
//                     <p>
//                       {values.plan} - $
//                       {values.plan === "eNotes Unlimited"
//                         ? "9.95"
//                         : values.plan === "Standard"
//                         ? "19.99"
//                         : "29.99"}
//                       /year
//                     </p>
//                   </div>

//                   <div className="review-card">
//                     <h4>User Information</h4>
//                     <p>
//                       {values.firstName} {values.lastName}
//                     </p>
//                     <p>{values.email}</p>
//                   </div>
//                   <div className="review-card">
//                     <h4>Payment Method</h4>
//                     <p>Card information securely provided.</p>
//                     <p>We do not store or display card details.</p>
//                   </div>
//                 </div>

//                 <div className="privacy-step">
//                   <AgreeTermsCheckbox
//                     id="agreeToPrivacy"
//                     // label="I agree to the Privacy Policy and Terms of Service."
//                     checked={values.agreeToPrivacy}
//                     onChange={(e) =>
//                       setFieldValue("agreeToPrivacy", e.target.checked)
//                     }
//                     error={touched.agreeToPrivacy && errors.agreeToPrivacy}
//                   />

//                   <p className="terms-message">
//                     By checking this box, you confirm that you have read and
//                     agree to our{" "}
//                     <a
//                       href="/privacy-policy"
//                       target="_blank"
//                       rel="noopener noreferrer"
//                     >
//                       Privacy Policy
//                     </a>{" "}
//                     and{" "}
//                     <a href="/terms" target="_blank" rel="noopener noreferrer">
//                       Terms of Service
//                     </a>
//                     . You also authorize <strong>enotesUnlimited</strong> to
//                     securely process your subscription payment of $
//                     {values.plan === "eNotes Unlimited"
//                       ? "9.95"
//                       : values.plan === "Standard"
//                       ? "19.99"
//                       : "29.99"}
//                     /year.
//                   </p>
//                   <p
//                     style={{
//                       fontSize: "13px",
//                       color: "#444",
//                       marginTop: "10px",
//                     }}
//                   >
//                     To verify your card, a temporary $1 authorization may appear
//                     on your bank statement. This is{" "}
//                     <strong>not a charge</strong> and will disappear
//                     automatically. Your actual subscription fee of{" "}
//                     <strong>$9.95</strong> will be processed after verification.
//                   </p>
//                 </div>
//               </>
//             )}

//             <div className="next-prev-button">
//               {step > 1 && (
//                 <button type="button" onClick={handleBack}>
//                   Back
//                 </button>
//               )}
//               <button
//                 type="submit"
//                 disabled={!isStepValid()}
//                 className={`next-button ${!isStepValid() ? "disabled" : ""}`}
//               >
//                 {step === 5 ? (
//                   <>
//                     <FiLock aria-hidden="true" /> Pay Now
//                   </>
//                 ) : (
//                   "Next"
//                 )}
//               </button>
//             </div>
//           </form>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default SubscriptionForm;
